'use client'

import { useEffect, useState } from 'react'
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider'
import { useSession } from '@wiggot/auth/hooks/useSession'
import Button from '@wiggui/ds/Button'
import GoogleIcon from '@wiggui/ds/GoogleIcon'
import LogoWiggot from '@wiggui/ds/LogoWiggot'
import { Tab, Tabs } from '@wiggui/ds/Tabs'
import { composeClasses } from '../../../../utils/src/strings'
import { EAmplitudeEvents } from '../amplitudEnums'
import Container from '../container/Container'
import { ProfileButton } from '../profile'
import { HomeSideMenu } from '../side-menu/SideMenu'
import { EAmplitudeEventsMinisitesLanding } from '../../../constants/minisites-events'
import { ROUTES } from '../../../constants'

interface IProps {
  whiteVariant?: boolean
  showMenuBurguer?: boolean
  showUserInfo?: boolean
  showLinks?: boolean
}

const URL_REDIRECT_LOGIN = ROUTES.AUTH_LOGIN
const URL_REDIRECT_SIGNUP = `${ROUTES.AUTH}/sign-up`

const NavbarHome = ({
  whiteVariant = false,
  showMenuBurguer = true,
  showUserInfo = true,
  showLinks = true
}: IProps) => {
  const { token } = useSession()
  const { trackEvent } = useAmplitude()
  const [showSideMenu, setShowSideMenu] = useState(false)

  const [scrolling, setScrolling] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const isNavbarWhiteVariant = scrolling || whiteVariant

  const handleScroll = () => {
    if (window.scrollY > 70) {
      setScrolling(true)
    } else {
      setScrolling(false)
    }
  }

  const handleGoTo = (route: string, activeTab: number) => {
    if (route.includes('crea-tu-sitio')) {
      trackEvent({
        name: EAmplitudeEventsMinisitesLanding.MINISITES_VIEW_SELECTED,
        eventProperties: {
          where: 'navbar',
          isLogin: Boolean(token),
          description: 'Tu sitio web'
        }
      })
    }
    if (route.includes('plans')) {
      trackEvent({
        name: EAmplitudeEvents.PLANS_ACTION,
        eventProperties: {
          where: 'navbar',
          isLogin: Boolean(token),
          description: 'Planes'
        }
      })
    }
    window.location.replace(`${window.location.origin}${route}`)
    setActiveTab(activeTab)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (window.location.pathname === '/plans') {
      setActiveTab(2)
    }

    if (window.location.pathname === '/crea-tu-sitio') {
      setActiveTab(1)
    }
  }, [])

  const handleLogin = () => {
    trackEvent({
      name: EAmplitudeEvents.LANDING_BEGIN,
      eventProperties: {
        where: 'navbar'
      }
    })
  }

  const handleEvent = () => {
    trackEvent({
      name: EAmplitudeEvents.LANDING_LOGIN,
      eventProperties: {
        where: 'navbar'
      }
    })
  }

  return (
    <>
      <HomeSideMenu show={showSideMenu} setShowSideMenu={setShowSideMenu} />
      <nav className="fixed w-full h-[70px] z-[2]">
        <Container
          classSection={composeClasses(
            isNavbarWhiteVariant
              ? 'border-b border-surface-strokes'
              : 'border-b border-primary backdrop-blur-[10px]',
            ''
          )}
          styleSection={{
            backgroundColor: isNavbarWhiteVariant ? '#FFF' : '#2222224D'
          }}
        >
          <div className="w-full h-[70px] flex-1 flex items-center justify-between relative">
            <button
              type="button"
              className="min-w-[120px] h-full"
              onClick={() => handleGoTo('/', 0)}
            >
              <LogoWiggot
                variant={isNavbarWhiteVariant ? 'black' : 'white'}
                width="120px"
              />
            </button>

            {showLinks && (
              <Tabs className="hidden md:flex h-full w-fit justify-center">
                <Tab
                  className={
                    isNavbarWhiteVariant ? 'text-primary' : 'text-white'
                  }
                  active={activeTab === 0}
                  onClick={() => handleGoTo('/', 0)}
                  title="Inicio"
                  activeBgColorClass="bg-white"
                  onHoverClass=""
                />
                <Tab
                  className={
                    isNavbarWhiteVariant ? 'text-primary' : 'text-white'
                  }
                  active={activeTab === 1}
                  onClick={() => handleGoTo('/crea-tu-sitio', 1)}
                  title="Tu sitio web"
                  activeBgColorClass="bg-white"
                  onHoverClass=""
                />
                <Tab
                  className={
                    isNavbarWhiteVariant ? 'text-primary' : 'text-white'
                  }
                  active={activeTab === 2}
                  onClick={() => handleGoTo('/plans', 2)}
                  title="Planes"
                  activeBgColorClass="bg-white"
                  onHoverClass=""
                />
              </Tabs>
            )}

            {showUserInfo && (
              <div className="absolute right-0">
                {token ? (
                  <div className="hidden lg:block">
                    <ProfileButton
                      variant={scrolling || whiteVariant ? 'white' : 'black'}
                    />
                  </div>
                ) : (
                  <div className="flex gap-6 items-center">
                    <a
                      onClick={handleEvent}
                      className={`text-white underline text-base font-medium w-fit hidden lg:flex ${
                        isNavbarWhiteVariant && '!text-black'
                      }`}
                      href={URL_REDIRECT_LOGIN}
                    >
                      Iniciar sesión
                    </a>
                    <a onClick={handleLogin} href={URL_REDIRECT_SIGNUP}>
                      <Button
                        className="hidden lg:flex"
                        variant={
                          isNavbarWhiteVariant ? 'secondary' : 'terciary'
                        }
                      >
                        Inicia tu prueba gratuita
                      </Button>
                    </a>
                  </div>
                )}
              </div>
            )}
            <span className="w-[120px]" />
            {showMenuBurguer && (
              <button
                type="button"
                onClick={() => setShowSideMenu(true)}
                className="lg:hidden flex justify-center items-center w-12 h-12 p-3"
              >
                <GoogleIcon
                  className={
                    isNavbarWhiteVariant ? 'text-primary' : 'text-white'
                  }
                  name="menu"
                />
              </button>
            )}
          </div>
        </Container>
      </nav>
    </>
  )
}

export default NavbarHome
